import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useBucketColours, BUCKET_LABELS, duration } from '../ExecutionStats/executionStatsVocabulary';
import { ExplainabilityDataElement, decisionChoices, mergeTechniques, severityTally, stageStatus } from './explainabilityTypes';
import SeverityChip from './SeverityChip';

/** Decisions listed on a card before the rest are left to the drawer. */
const DECISIONS_SHOWN = 3;

const inter = { fontFamily: '"Inter", sans-serif' };

interface StageCardProps {
    stage: ExplainabilityDataElement;
    position: number;
    selected: boolean;
    onOpen: () => void;
}

/**
 * What one stage of the flow did: its outcome, the techniques it ran and the
 * choices it made. The observations and reasoning behind them open in the drawer.
 */
const StageCard: React.FC<StageCardProps> = ({ stage, position, selected, onOpen }) => {
    const bucketColours = useBucketColours();
    const { outcome } = stage;
    const status = stageStatus(outcome);
    const rollup = stage.customer_rollup;
    const tally = severityTally(rollup?.observations);
    const techniques = mergeTechniques(stage.techniques_applied);
    const choices = decisionChoices(stage);
    const observations = stage.trace_counts?.observations ?? stage.explainability_traces.length;
    const decisions = stage.trace_counts?.decisions ?? choices.length;

    const meta = [
        rollup ? `${rollup.customers_processed} ${rollup.customers_processed === 1 ? 'customer' : 'customers'}` : null,
        outcome?.duration_secs !== undefined ? duration(outcome.duration_secs * 1000) : null,
        `${observations} ${observations === 1 ? 'observation' : 'observations'}`,
        `${decisions} ${decisions === 1 ? 'decision' : 'decisions'}`,
    ].filter(Boolean);

    return (
        <button
            type="button"
            onClick={onOpen}
            aria-pressed={selected}
            className={`group w-full text-left bg-surface rounded-2xl border px-6 py-4 cursor-pointer transition-all ${selected
                ? 'border-accent-fg shadow-lg shadow-accent/10'
                : 'border-line hover:border-accent-fg/40 hover:bg-raised/40'
                }`}
            style={inter}
        >
            <div className="flex items-start gap-4">
                <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-xs font-bold ${selected ? 'bg-accent text-on-accent' : 'bg-line text-accent-fg'}`}>
                    {position}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                        <h4 className="text-sm font-semibold text-fg m-0">{stage.stage}</h4>
                        <ChevronRightIcon className={`shrink-0 transition-colors ${selected ? 'text-accent-fg' : 'text-fg-faint group-hover:text-fg-soft'}`} fontSize="small" />
                    </div>

                    <p className="flex items-center flex-wrap gap-x-2 text-[10px] text-fg-muted font-medium uppercase tracking-wider m-0 mt-0.5">
                        {status && (
                            <span className="flex items-center gap-1" title={status.raw ? `Recorded as "${status.raw}"` : undefined}>
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: status.bucket ? bucketColours[status.bucket] : '#475569' }} />
                                {status.raw && status.bucket ? BUCKET_LABELS[status.bucket] : status.label}
                            </span>
                        )}
                        {meta.map((part, i) => (
                            <span key={i}>{status || i > 0 ? '· ' : ''}{part}</span>
                        ))}
                    </p>

                    {outcome?.nl_description && (
                        <p className="text-sm text-fg-soft leading-relaxed m-0 mt-2">{outcome.nl_description}</p>
                    )}

                    {tally.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            <span className="text-xs text-fg-2">Observations:</span>
                            {tally.map(([severity, n]) => <SeverityChip key={severity} severity={severity} count={n} />)}
                        </div>
                    )}

                    {techniques.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {techniques.map((tech, i) => (
                                <span key={`${tech.technique_name}-${i}`} className="text-[11px] text-fg-soft bg-page border border-line rounded-md px-2 py-0.5">
                                    {tech.technique_name}{tech.times > 1 && <span className="text-fg-muted"> ×{tech.times}</span>}
                                </span>
                            ))}
                        </div>
                    )}

                    {choices.length > 0 && (
                        <ul className="list-none m-0 p-0 mt-3 space-y-1">
                            {choices.slice(0, DECISIONS_SHOWN).map((choice) => (
                                <li key={choice.id} className="text-xs text-fg-2 flex gap-1.5 min-w-0">
                                    <span className="text-emerald-400/80 shrink-0">↳</span>
                                    <span className="truncate">
                                        {choice.type}
                                        {choice.choice && <span className="text-fg-body"> → {choice.choice}</span>}
                                    </span>
                                </li>
                            ))}
                            {choices.length > DECISIONS_SHOWN && (
                                <li className="text-xs text-fg-muted pl-4">+{choices.length - DECISIONS_SHOWN} more in details</li>
                            )}
                        </ul>
                    )}
                </div>
            </div>
        </button>
    );
};

export default StageCard;
